import { htmlShowsPaymentReceivedPage } from './payment-received-page.js';
import {
    getManagerOverlayElement,
    getMemberOverlayElement,
    hideOverlay,
} from './overlay.js';
import { lockPayButtonsAfterCharge } from './payment-pay-button.js';

const COMPLETED_ATTR = 'data-kiosk-payment-completed';
const FALLBACK_BANNER_ID = 'kiosk-payment-completed-banner';
const PAY_CONTROL_SELECTOR = [
    '.manual-card-form button[type="submit"]',
    '.manual-card-form input',
    '.manual-card-form select',
    '[data-payment-pay]',
    '#swipeButton',
    '.user-saved-payment-next-btn',
    '#open_balance_save_btn',
    '#payAuth',
].join(',');

export function normalizeChargeResponse(response) {
    if (response == null) {
        return {};
    }
    if (typeof response === 'string') {
        const trimmed = response.trim();
        if (trimmed.charAt(0) === '{') {
            try {
                return normalizeChargeResponse(JSON.parse(trimmed));
            } catch (err) {
                console.error('normalizeChargeResponse: JSON parse failed', err);
            }
        }
        const looksReceived = htmlShowsPaymentReceivedPage(trimmed);
        return {
            success: looksReceived,
            payment_processed: looksReceived,
            page_info: trimmed,
        };
    }
    if (typeof response !== 'object') {
        return {};
    }
    return response;
}

export function fallbackPaymentReceivedHtml() {
    return (
        '<div class="row main_start w-100 donation-flow payment-received-page" data-payment-received-fallback="1">' +
            '<div class="col-12 donation-flow__container">' +
                '<div class="payment-received-card">' +
                    '<h1 class="payment-received-card__title">Donation Received</h1>' +
                    '<p class="payment-received-card__subtitle">Your payment went through. Do not submit payment again.</p>' +
                    '<div class="payment-received-card__actions">' +
                        '<button type="button" class="action-btn press-feedback press-feedback-round payment-received-card__action" data-payment-received-action="home">' +
                            'Return to homepage' +
                        '</button>' +
                    '</div>' +
                '</div>' +
            '</div>' +
        '</div>'
    );
}

export function spaChargeSuccessRoot() {
    return document.getElementById('mgr_index_paging') || document.getElementById('index_paging');
}

function markDocumentCompleted() {
    document.documentElement.setAttribute(COMPLETED_ATTR, '1');
}

function disableRemainingPayControls() {
    document.querySelectorAll(PAY_CONTROL_SELECTOR).forEach((el) => {
        el.disabled = true;
    });
}

function lockAgainstResubmit() {
    markDocumentCompleted();
    try {
        lockPayButtonsAfterCharge();
    } catch (err) {
        console.error('applyChargeSuccess: lockPayButtonsAfterCharge failed', err);
    }
    disableRemainingPayControls();
}

function writeHtml(root, html) {
    if (!root) {
        throw new Error('applyChargeSuccess: missing spa root');
    }
    if (typeof html !== 'string') {
        throw new Error('applyChargeSuccess: page html must be a string');
    }
    const $ = window.jQuery;
    if ($ && root.id) {
        const $root = $('#' + root.id);
        if ($root.length) {
            $root.empty().html(html).fadeIn('slow');
            return;
        }
    }
    root.innerHTML = html;
}

function successPageVisible() {
    return document.querySelector('.payment-received-page') !== null;
}

function spaRootHasContent(root) {
    return !!(root && String(root.innerHTML || '').trim());
}

function payControlsStillInSpaRoot(root) {
    if (!root) {
        return document.querySelector('.manual-card-form, [data-payment-pay]') !== null;
    }
    return root.querySelector('.manual-card-form, [data-payment-pay], #swipeButton') !== null;
}

function shouldShowFallback(root) {
    if (successPageVisible()) {
        return false;
    }
    if (payControlsStillInSpaRoot(root)) {
        return true;
    }
    return !spaRootHasContent(root);
}

function mountBlockingBanner() {
    if (document.getElementById(FALLBACK_BANNER_ID)) {
        return;
    }
    const banner = document.createElement('div');
    banner.id = FALLBACK_BANNER_ID;
    banner.className = 'payment-received-page';
    banner.setAttribute('role', 'alert');
    banner.innerHTML = fallbackPaymentReceivedHtml();
    document.body.insertBefore(banner, document.body.firstChild);
}

function hideChargeOverlays() {
    try {
        hideOverlay(getMemberOverlayElement());
    } catch (err) {
        console.error('applyChargeSuccess: hide member overlay failed', err);
    }
    try {
        hideOverlay(getManagerOverlayElement());
    } catch (err) {
        console.error('applyChargeSuccess: hide manager overlay failed', err);
    }
}

function tryHardRedirect() {
    try {
        window.location.replace('/payment-received');
        return true;
    } catch (err) {
        console.error('applyChargeSuccess: location.replace failed', err);
        return false;
    }
}

export function applyChargeSuccess(response, options) {
    const opts = options || {};
    const normalized = normalizeChargeResponse(response);
    lockAgainstResubmit();

    const pageInfo = typeof normalized.page_info === 'string' ? normalized.page_info : '';
    let root = spaChargeSuccessRoot();

    try {
        if (pageInfo !== '') {
            writeHtml(root, pageInfo);
        }
    } catch (err) {
        console.error('applyChargeSuccess: inject page_info failed', err);
    }

    root = spaChargeSuccessRoot();

    if (shouldShowFallback(root)) {
        try {
            writeHtml(root, fallbackPaymentReceivedHtml());
        } catch (err) {
            console.error('applyChargeSuccess: fallback inject failed', err);
            mountBlockingBanner();
            tryHardRedirect();
        }
    }

    if (!successPageVisible() && !spaRootHasContent(spaChargeSuccessRoot()) && !document.getElementById(FALLBACK_BANNER_ID)) {
        mountBlockingBanner();
        tryHardRedirect();
    }

    lockAgainstResubmit();

    if (typeof opts.afterInject === 'function') {
        try {
            opts.afterInject(normalized);
        } catch (err) {
            console.error('applyChargeSuccess: afterInject failed', err);
        }
    }

    hideChargeOverlays();
    return normalized;
}
